// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xGAUpdate } from "@adl/App/Handlers/GoogleAnalyticsHandlers/GA/xGA";
import { xGaState } from "@adl/App/Store/Reducers";
import { getDataLayer, xIsAuthenticated } from "@adl/XDL/Utilities";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedProductPath, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getComputedVariantProduct,
    getProductsDataView,
    getProductState,
    getProductStateByPath,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import displayUser from "@insite/client-framework/Store/Pages/UserSetup/Handlers/DisplayUser";
import { ProductModel, ProductPriceDto, ProductPriceModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

type ProductData = Pick<ProductModel, "productNumber" | "productTitle" | "brand" | "properties"> & {
    pricing: ProductPriceDto | undefined;
    qtyOrdered: number;
};

const mapStateToProps = (state: ApplicationState & xGaState) => {
    let PDPProduct: ProductData | null = null;
    let PLPProducts: ProductData[] = [];

    const currentPage = getCurrentPage(state).type;
    const location = getLocation(state);

    const isPDP = currentPage === "ProductDetailsPage";
    const isPLP = currentPage === "ProductListPage";
    const isCartPage = currentPage === "CartPage";
    let productInfo;
    if (isPDP) {
        const productPath =
            getSelectedProductPath(state) ||
            (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
        const productState = getProductStateByPath(state, productPath);
        const variantProductState = getProductState(state, state.pages.productDetails.selectedProductId);
        const computedVariantProduct = getComputedVariantProduct(productState, variantProductState);
        productInfo =
            computedVariantProduct.value && state.pages.productDetails.productInfosById
                ? state.pages.productDetails.productInfosById[computedVariantProduct.value.id]
                : undefined;
                
        // XNG-Changes: GA4
        if (productState?.value) {
            const { productNumber, productTitle, brand, properties } = productState.value;
            PDPProduct = {
                productNumber,
                productTitle,
                brand,
                properties,
                pricing: productInfo?.pricing as ProductPriceDto,
                qtyOrdered: productInfo?.qtyOrdered || 1,
            };
        }
    }

    if (isPLP) {
        const {
            pages: {
                productList: { parameter },
            },
        } = state;
        const productsDataView = getProductsDataView(state, parameter);

        if (productsDataView && productsDataView.value) {
            PLPProducts = [];

            productsDataView.value.forEach(product => {
                const productInfo = state.pages.productList.productInfosByProductId[product.id];
                
                if (productInfo?.pricing) {
                    PLPProducts.push({ ...product, pricing: productInfo?.pricing, qtyOrdered: productInfo.qtyOrdered });
                }
            });
        }
    }

    return {
        productInfo,
        currentPage,
        PDPProduct,
        PLPProducts,
        xGAState: state.xGA,
        currencyCode: state.context?.session?.currency?.currencyCode,
        isAuthenticated: xIsAuthenticated(state),
        account: getCurrentAccountState(state),
        shipTo: getShipToState(state, state.context?.session.shipToId).value,
        billTo: getBillToState(state, state.context?.session.billToId).value,
        location,
        isCartPage,
        isPDP,
        isPLP,
        cart: getCurrentCartState(state),
        session: getSession(state),
    };
};

const mapDispatchToProps = {
    xGAUpdate,
    displayUser,
};

const enum fields {
    checkoutFlowEvent = "checkoutFlowEvent",
    checkoutFlowEventName = "checkoutFlowEventName",
    addToCartEvent = "addToCartEvent",
    addToCartEventName = "addToCartEventName",
    cartToBuyRateEvent = "cartToBuyRateEvent",
    transactionEventName = "transactionEventName",
    removeFromCartLineEvent = "removeFromCartLineEvent",
    removeFromCartLineEventName = "removeFromCartLineEventName",
    pdpViewEvent = "pdpViewEvent",
    pdpViewEventName = "pdpViewEventName",
    productImpressionEvent = "productImpressionEvent",
    productImpressionEventName = "productImpressionEventName",
    userInfoEvent = "userInfoEvent",
    userInfoEventName = "userInfoEventName",
    productBrand = "productBrand",
    productCategory = "productCategory",
    signInEvent = "signInEvent",
    signInEventName = "signInEventName",
    viewCartEvent = "viewCartEvent",
    viewCartEventName = "viewCartEventName",
    selectItemEvent = "selectItemEvent",
    selectItemEventName = "selectItemEventName",
    viewItemListEvent = "viewItemListEvent",
    viewItemListEventName = "viewItemListEventName",
    generateCustomQuote = "generateCustomQuote",
    generateCustomQuoteEventName = "generateCustomQuoteEventName",
    submitBid = "submitBid",
    submitBidEventName = "submitBidEventName",
    acceptBid = "acceptBid",
    acceptBidEventName = "acceptBidEventName",
    invoicePayment = "invoicePayment",
    invoicePaymentEventName = "invoicePaymentEventName",
    invoiceHistoryPageLoadEvent = "invoiceHistoryPageLoadEvent",
    invoiceHistoryPageLoadEventName = "invoiceHistoryPageLoadEventName",
    invoiceDetailsPageLoadEvent = "invoiceDetailsPageLoadEvent",
    invoiceDetailsPageLoadEventName = "invoiceDetailsPageLoadEventName",
    orderHistoryPageLoadEvent = "orderHistoryPageLoadEvent",
    orderHistoryPageLoadEventName = "orderHistoryPageLoadEventName",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.checkoutFlowEvent]: boolean;
        [fields.checkoutFlowEventName]: string;
        [fields.addToCartEvent]: boolean;
        [fields.addToCartEventName]: string;
        [fields.cartToBuyRateEvent]: boolean;
        [fields.transactionEventName]: string;
        [fields.removeFromCartLineEvent]: boolean;
        [fields.removeFromCartLineEventName]: string;
        [fields.pdpViewEvent]: boolean;
        [fields.pdpViewEventName]: string;
        [fields.productImpressionEvent]: boolean;
        [fields.productImpressionEventName]: string;
        [fields.userInfoEvent]: boolean;
        [fields.userInfoEventName]: string;
        [fields.productBrand]: boolean;
        [fields.productCategory]: boolean;
        [fields.signInEvent]: boolean;
        [fields.signInEventName]: string;
        [fields.viewCartEvent]: boolean;
        [fields.viewCartEventName]: string;
        [fields.selectItemEvent]: boolean;
        [fields.selectItemEventName]: string;
        [fields.viewItemListEvent]: boolean;
        [fields.viewItemListEventName]: string;
        [fields.generateCustomQuote]: boolean;
        [fields.generateCustomQuoteEventName]: string;
        [fields.submitBid]: boolean;
        [fields.submitBidEventName]: string;
        [fields.acceptBid]: boolean;
        [fields.acceptBidEventName]: string;
        [fields.invoicePayment]: boolean;
        [fields.invoicePaymentEventName]: string;
        [fields.invoiceHistoryPageLoadEvent]: boolean;
        [fields.invoiceHistoryPageLoadEventName]: string;
        [fields.invoiceDetailsPageLoadEvent]: boolean;
        [fields.invoiceDetailsPageLoadEventName]: string;
        [fields.orderHistoryPageLoadEvent]: boolean;
        [fields.orderHistoryPageLoadEventName]: string;
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

const GoogleAnalyticsWidget: FC<Props> = ({
    xGAUpdate,
    fields,
    currentPage,
    productInfo,
    PDPProduct,
    PLPProducts,
    xGAState,
    currencyCode,
    shellContext,
    isAuthenticated,
    account,
    shipTo,
    billTo,
    location,
    displayUser,
    isCartPage,
    isPDP,
    isPLP,
    cart,
    session,
}) => {
    if (shellContext.isInShell) {
        // to display a place holder when authoring the widget
        return <i>GoogleAnalytics Element</i>;
    }
    const [PDPInfoLoaded, setPDPInfoLoaded] = useState<boolean>(false);
    const [PLPInfoLoaded, setPLPInfoLoaded] = useState<boolean>(false);
    // XNG-Changes: save the current product & products list
    const [currentProduct, setCurrentProduct] = useState(PDPProduct);
    const [currentPLP, setCurrentPLP] = useState(PLPProducts);
    const [productPricing, setProductPricing] = useState<ProductPriceDto>();
    
    useEffect(() => {
        if(productInfo?.pricing) {
            setProductPricing(productInfo.pricing);
        }
    }, [productInfo]);

    useEffect(() => {
        xGAUpdate({
            checkoutFlowEvent: fields["checkoutFlowEvent"] || false,
            checkoutFlowEventName: fields["checkoutFlowEventName"] || "checkout",
            addToCartEvent: fields["addToCartEvent"] || false,
            addToCartEventName: fields["addToCartEventName"] || "addToCart",
            cartToBuyRateEvent: fields["cartToBuyRateEvent"] || false,
            transactionEventName: fields["transactionEventName"] || "purchase",
            removeFromCartLineEvent: fields["removeFromCartLineEvent"] || false,
            removeFromCartLineEventName: fields["removeFromCartLineEventName"] || "removeFromCart",
            pdpViewEvent: fields["pdpViewEvent"] || false,
            pdpViewEventName: fields["pdpViewEventName"] || "productDetails",
            productImpressionEvent: fields["productImpressionEvent"] || false,
            productImpressionEventName: fields["productImpressionEventName"] || "productImpression",
            userInfoEvent: fields["userInfoEvent"] || false,
            userInfoEventName: fields["userInfoEventName"] || "userInfo",
            productBrand: fields["productBrand"] || false,
            productCategory: fields["productCategory"] || false,
            signInEvent: fields["signInEvent"] || false,
            signInEventName: fields["signInEventName"] || "login",
            viewCartEvent: fields["viewCartEvent"] || false,
            viewCartEventName: fields["viewCartEventName"] || "view_cart",
            selectItemEvent: fields["selectItemEvent"] || false,
            selectItemEventName: fields["selectItemEventName"] || "select_item",
            viewItemListEvent: fields["viewItemListEvent"] || false,
            viewItemListEventName: fields["viewItemListEventName"] || "view_item_list",
            generateCustomQuote: fields["generateCustomQuote"] || false,
            generateCustomQuoteEventName: fields["generateCustomQuoteEventName"] || "generate_custom_quote",
            submitBid: fields["submitBid"] || false,
            submitBidEventName: fields["submitBidEventName"] || "selfservice_submitbid",
            acceptBid: fields["acceptBid"] || false,
            acceptBidEventName: fields["acceptBidEventName"] || "selfservice_acceptsbid",
            invoicePayment: fields["invoicePayment"] || false,
            invoicePaymentEventName: fields["invoicePaymentEventName"] || "selfservice_invoicepayment",
            invoiceHistoryPageLoadEvent: fields["invoiceHistoryPageLoadEvent"] || false,
            invoiceHistoryPageLoadEventName: fields["invoiceHistoryPageLoadEventName"] || "selfservice_invoicehistory",
            invoiceDetailsPageLoadEvent: fields["invoiceDetailsPageLoadEvent"] || false,
            invoiceDetailsPageLoadEventName: fields["invoiceDetailsPageLoadEventName"] || "selfservice_invoicedetails",
            orderHistoryPageLoadEvent: fields["orderHistoryPageLoadEvent"] || false,
            orderHistoryPageLoadEventName: fields["orderHistoryPageLoadEventName"] || "selfservice_orderhistory",
        });
    }, [fields]);

    useEffect(() => {
        if (isAuthenticated && !account?.value && !account?.isLoading) {
            displayUser({ userId: "current" });
            return;
        }
        const dataLayer = getDataLayer();

        if (dataLayer && xGAState.userInfoEvent && !account?.isLoading) {
            const data = {
                event: xGAState.userInfoEventName || "userInfo",
                authenticated: isAuthenticated ? "true" : "false",

                user_id: account.value?.properties?.eclipseId || "",

                shipto: {
                    number: shipTo?.customerNumber || "",
                    sequence: shipTo?.customerSequence || "",
                },
                billto: {
                    number: billTo?.customerNumber || "",
                    sequence: billTo?.customerSequence || "",
                },
            } as any;

            dataLayer.push(data);
        }
    }, [isAuthenticated, account?.value?.properties?.eclipseId, shipTo?.id, billTo?.id, xGAState, location]);

    // add login event if exist
    useEffect(() => {
        const dataLayer = getDataLayer();
        if (dataLayer && xGAState.signInEvent && isAuthenticated) {
            const data = {
                event: xGAState.signInEventName || "login",
            };
            dataLayer.push(data);
        }
    }, [xGAState]);

    // XNG-Changes: ADL-1192
    // add the pdpEvent to the dataLayer if it's different product

    useEffect(() => {
        if (!currentProduct && PDPProduct) {
            setCurrentProduct(PDPProduct);
        }
        if (currentProduct && PDPProduct && PDPProduct.productNumber !== currentProduct.productNumber) {
            setPDPInfoLoaded(false);
        }
    }, [PDPProduct]);

    // XNG-Changes: ADL-1192
    // add the productImpressionEvent to dataLayer if it's a new PLP
    useEffect(() => {
        if (!currentPLP && PLPProducts) {
            setCurrentPLP(PLPProducts);
        }
        if (currentPLP && PLPProducts) {
            if (currentPLP.length !== PLPProducts.length) {
                setPLPInfoLoaded(false);
                return;
            }

            const areSameProducts = currentPLP.every((product: any, index: number) => {
                return product.productNumber === PLPProducts[index].productNumber;
            });

            if (!areSameProducts) {
                setPLPInfoLoaded(false);
            }
        }
    }, [PLPProducts]);

    // PLP event handler
    useEffect(() => {
        const dataLayer = getDataLayer();

        if (dataLayer) {
            if (isPDP && xGAState.pdpViewEvent && PDPProduct && !PDPInfoLoaded && ((isAuthenticated && productPricing) || !isAuthenticated)) {
                setPDPInfoLoaded(true);
                
                // XNG-Changes: Created New Custom Event for PDP View
                const data = {
                    event: xGAState.pdpViewEventName || "view_item",
                    ecommerce: {
                        currency: session.currency?.currencyCode,
                        value: productPricing?.unitNetPrice,
                        items: [
                            {
                                item_id: PDPProduct?.productNumber,
                                item_name: PDPProduct?.productTitle,
                                price: productPricing?.unitNetPrice,
                                quantity: PDPProduct.qtyOrdered,
                                location_id: session.pickUpWarehouse?.id || "",
                            },
                        ],
                    },
                } as any;

                if(productInfo?.pricing && (productInfo?.pricing.unitListPrice - productInfo.pricing?.unitNetPrice > 0)) {
                    data["ecommerce"]["items"][0]["discount"] = productInfo.pricing?.unitListPrice - productInfo.pricing?.unitNetPrice;
                }

                if (xGAState["productBrand"]) {
                    data["ecommerce"]["items"][0]["item_brand"] = PDPProduct.brand?.name || "";
                }

                if (xGAState["productCategory"]) {
                    data["ecommerce"]["items"][0]["item_category"] = PDPProduct.properties["category"] || "";
                }

                /**
                 * ADL-1143
                 * Sometimes Sandbox shows duplicate view_item event in dataLayer
                 * below code pushes the new event if last event in datalayer is not view_item
                 * and the last event ecommerce is different than the new one
                 * meaning is related to another product / part number
                 */

                const lastDataLayerEvent = dataLayer[dataLayer.length - 1];
                if (
                    !lastDataLayerEvent.ecommerce ||
                    lastDataLayerEvent.ecommerce?.items[0].item_id !== data.ecommerce.items[0].item_id
                ) {
                    dataLayer.push(data);
                }
            }

            if (isPLP && xGAState.productImpressionEvent && PLPProducts?.length && !PLPInfoLoaded) {
                setPLPInfoLoaded(true);

                const data = {
                    event: xGAState.productImpressionEventName || "productImpression",
                    ecommerce: {
                        currencyCode: currencyCode || "USD",
                        impressions: [] as any,
                    },
                };

                PLPProducts.forEach((product: any, idx: number) => {
                    const temp: any = {
                        name: product?.productTitle,
                        id: product?.productNumber,
                        price: product.pricing?.unitNetPrice,
                        position: idx,
                    };

                    if (xGAState["productBrand"]) {
                        temp["brand"] = product.brand?.name || "";
                    }

                    if (xGAState["productCategory"]) {
                        temp["category"] = product.properties["category"] || "";
                    }

                    data.ecommerce.impressions.push(temp);
                });

                dataLayer.push(data);
            }
        }
    }, [PLPProducts, PDPProduct, currentPage, xGAState, productPricing]);

    useEffect(() => {
        const dataLayer = getDataLayer();

        if (!cart || !cart.value || cart.isLoading) {
            return;
        }

        if (isCartPage && xGAState.viewCartEvent) {
            setPDPInfoLoaded(false);
            setPLPInfoLoaded(false);

            const googleAnalyticsData = {
                event: xGAState.viewCartEventName || "view_cart",
                value: cart.value.orderSubTotal,
                currency: currencyCode,
                items: cart.value?.cartLines?.map((cartItem, idx) => ({
                    item_name: cartItem.productName,
                    item_id: cartItem.productId,
                    price: cartItem.pricing?.unitNetPrice,
                    quantity: cartItem.qtyOrdered,
                    index: idx,
                    item_brand: xGAState["productBrand"] ? cartItem.brand?.name || "" : undefined,
                    item_category: xGAState["productCategory"] ? cartItem.properties?.["category"] || "" : undefined,
                    item_list_id: xGAState["productCategory"] ? cartItem.properties?.["categoryId"] || "" : undefined,
                })),
            };

            dataLayer.push(googleAnalyticsData);
        }
    }, [currentPage, cart]);

    return null;
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(GoogleAnalyticsWidget)),
    definition: {
        displayName: "Google Analytics Management",
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.checkoutFlowEvent,
                displayName: "Checkout Flow (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.checkoutFlowEventName,
                displayName: "Checkout Flow Event Name",
                editorTemplate: "TextField",
                defaultValue: "checkout",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.addToCartEvent,
                displayName: "Add To Cart (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.addToCartEventName,
                displayName: "Add To Cart Event Name",
                editorTemplate: "TextField",
                defaultValue: "add_to_cart",
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.cartToBuyRateEvent,
                displayName: "Cart To Buy Rate (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.transactionEventName,
                displayName: "Cart To Buy Rate Event Name",
                editorTemplate: "TextField",
                defaultValue: "purchase",
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.removeFromCartLineEvent,
                displayName: "Remove From Cart Line (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.removeFromCartLineEventName,
                displayName: "Remove From Cart Line Event Name",
                editorTemplate: "TextField",
                defaultValue: "removeFromCart",
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.pdpViewEvent,
                displayName: "PDP View (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.pdpViewEventName,
                displayName: "PDP View Event Name",
                editorTemplate: "TextField",
                defaultValue: "view_item",
                fieldType: "General",
                sortOrder: 10,
            },
            {
                name: fields.productImpressionEvent,
                displayName: "Product Impression (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 11,
            },
            {
                name: fields.productImpressionEventName,
                displayName: "Product Impression Event Name",
                editorTemplate: "TextField",
                defaultValue: "productImpression",
                fieldType: "General",
                sortOrder: 12,
            },
            {
                name: fields.userInfoEvent,
                displayName: "User Info (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 13,
            },
            {
                name: fields.userInfoEventName,
                displayName: "User Info Event Name",
                editorTemplate: "TextField",
                defaultValue: "user_info",
                fieldType: "General",
                sortOrder: 14,
            },
            {
                name: fields.signInEvent,
                displayName: "SignIn Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 15,
            },
            {
                name: fields.signInEventName,
                displayName: "Sign In Event Name",
                editorTemplate: "TextField",
                defaultValue: "login",
                fieldType: "General",
                sortOrder: 16,
            },
            {
                name: fields.viewCartEvent,
                displayName: "Cart View (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 17,
            },
            {
                name: fields.viewCartEventName,
                displayName: "View Cart Event Name",
                editorTemplate: "TextField",
                defaultValue: "view_cart",
                fieldType: "General",
                sortOrder: 18,
            },
            {
                name: fields.selectItemEvent,
                displayName: "Select Item (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 19,
            },
            {
                name: fields.selectItemEventName,
                displayName: "Select Item Event Name",
                editorTemplate: "TextField",
                defaultValue: "select_item",
                fieldType: "General",
                sortOrder: 20,
            },
            {
                name: fields.viewItemListEvent,
                displayName: "View Item List (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 21,
            },
            {
                name: fields.viewItemListEventName,
                displayName: "View Item List Event Name",
                editorTemplate: "TextField",
                defaultValue: "view_item_list",
                fieldType: "General",
                sortOrder: 22,
            },
            {
                name: fields.generateCustomQuote,
                displayName: "Generate Custom Quote (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 23,
            },
            {
                name: fields.generateCustomQuoteEventName,
                displayName: "Generate Custom Event Name",
                editorTemplate: "TextField",
                defaultValue: "generate_custom_quote",
                fieldType: "General",
                sortOrder: 24,
            },
            {
                name: fields.submitBid,
                displayName: "Submit Bid (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 25,
            },
            {
                name: fields.submitBidEventName,
                displayName: "Submit Bid Event Name",
                editorTemplate: "TextField",
                defaultValue: "selfservice_submitbid",
                fieldType: "General",
                sortOrder: 26,
            },
            {
                name: fields.acceptBid,
                displayName: "Accept Bid (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 27,
            },
            {
                name: fields.acceptBidEventName,
                displayName: "Accept Bid Event Name",
                editorTemplate: "TextField",
                defaultValue: "selfservice_acceptsbid",
                fieldType: "General",
                sortOrder: 28,
            },
            {
                name: fields.invoicePayment,
                displayName: "Invoice Payment (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 29,
            },
            {
                name: fields.invoicePaymentEventName,
                displayName: "Invoice Payment Event Name",
                editorTemplate: "TextField",
                defaultValue: "selfservice_invoicepayment",
                fieldType: "General",
                sortOrder: 30,
            },
            {
                name: fields.invoiceHistoryPageLoadEvent,
                displayName: "Invoice History Page Load (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 31,
            },
            {
                name: fields.invoiceHistoryPageLoadEventName,
                displayName: "Invoice History Page Load Event Name",
                editorTemplate: "TextField",
                defaultValue: "selfservice_invoicehistory",
                fieldType: "General",
                sortOrder: 32,
            },
            {
                name: fields.invoiceDetailsPageLoadEvent,
                displayName: "Invoice Details Page Load (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 33,
            },
            {
                name: fields.invoiceDetailsPageLoadEventName,
                displayName: "Invoice Details Page Load Event Name",
                editorTemplate: "TextField",
                defaultValue: "selfservice_invoicedetails",
                fieldType: "General",
                sortOrder: 34,
            },
            {
                name: fields.orderHistoryPageLoadEvent,
                displayName: "Order History Page Load (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 35,
            },
            {
                name: fields.orderHistoryPageLoadEventName,
                displayName: "Order History Page Load Event Name",
                editorTemplate: "TextField",
                defaultValue: "selfservice_orderhistory",
                fieldType: "General",
                sortOrder: 36,
            },
            {
                name: fields.productBrand,
                displayName: "Include Product Brand (data)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 37,
            },
            {
                name: fields.productCategory,
                displayName: "Include Product Category (data)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 38,
            },
        ],
    },
};

export default widgetModule;
